"use client";

import Grid from "@mui/material/Grid2";
import jwt_decode from "jwt-decode";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import BiometricPrompt from "@/components/BiometricPrompt";
import LeftContent from "@/components/screens/Login/components/LeftContent";
import BiometricUnlockForm from "@/components/screens/Login/components/BiometricUnlockForm";

import LOCAL_STORAGE from "@/constants/local-storage";
import ROUTE_URLS from "@/constants/route-urls";
import { ExpiredTokenError, InvalidTokenError, UserNotFound } from "@/errors/auth.errors";
import {
  checkIfTokenIsExpired,
  decodeToken,
  fetchUser,
  generateDecodedToken,
  getAccessToken,
  handleUserRedirects,
  performUnauthenticatedRedirect,
} from "@/helpers/auth-helpers";
import { buildErrorMessage } from "@/helpers/error-helpers";
import useLoading from "@/hooks/use-loading";
import useIdleTimeout from "@/hooks/use-idle-timeout";
import useProactiveTokenRefresh from "@/hooks/use-proactive-token-refresh";
import { logSessionEvent } from "@/utils/session-log";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import IUser from "@/interfaces/user.interface";
import authService from "@/services/auth.service";
import biometricAuthService from "@/services/biometric-auth.service";
import pushNotificationService from "@/services/push-notification.service";
import { userService } from "@/services/user.service";
import { isPWAStandalone } from "@/utils/environment.utils";
import {
  AuthContextProps,
  AuthProviderProps,
  ConfirmEmailProps,
  LoginProps,
  RegisterProps,
} from "@/types/auth.types";
import { UserProfile } from "@/types/user.types";

import useLanguage from "./language-context";
import { NotificationProvider } from "./notification-context";
import useToast from "./toast-context";

export const AuthContext = createContext<AuthContextProps>({} as AuthContextProps);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const router = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const { lang } = useLanguage();
  const { showToast } = useToast();

  // Verificar se as dependências estão disponíveis
  if (!router || !location || !lang || !showToast) {
    console.error("AuthProvider dependencies are not properly initialized");
    throw new Error("AuthProvider dependencies are missing");
  }

  const [searchParams] = useSearchParams();
  const redirectUrl = searchParams.get("redirect");
  const urlToRedirect =
    redirectUrl ||
    (isPWAStandalone() ? ROUTE_URLS.chat(lang) : ROUTE_URLS.serviceDashboard(lang));

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUserState] = useState<IUser | null>(null);
  const [isAuthReady, setIsAuthReady] = useState(false);

  const setUser: React.Dispatch<React.SetStateAction<IUser | null>> = useCallback((value) => {
    setUserState((prev) => {
      // eslint-disable-next-line prettier/prettier
      const next =
        typeof value === "function" ? (value as (p: IUser | null) => IUser | null)(prev) : value;
      return next;
    });
  }, []);
  const [isPasswordExpired, setIsPasswordExpired] = useState(false);
  const [isEmailConfirmed, setIsEmailConfirmed] = useState(true);
  const [redirectUrlLogout, setRedirectUrlLogout] = useState("");

  // PWA biometric states
  const [showBiometricPrompt, setShowBiometricPrompt] = useState(false);
  const [isBiometricLocked, setIsBiometricLocked] = useState(false);
  const [isBiometricAuthenticating, setIsBiometricAuthenticating] = useState(false);
  const [isBiometricTransitioning, setIsBiometricTransitioning] = useState(false);
  const isPWA = isPWAStandalone();

  // PWA: Check if biometric lock should be shown on startup
  useEffect(() => {
    if (!isPWA) return;
    const accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
    if (accessToken && biometricAuthService.isEnabled()) {
      setIsBiometricLocked(true);
    }
  }, [isPWA]);

  // PWA: Request push notification permission on first authenticated session
  useEffect(() => {
    if (!isPWA || !isAuthenticated) return;
    if (pushNotificationService.isSupported() && !pushNotificationService.isPermissionGranted()) {
      pushNotificationService.requestPermission();
    }
  }, [isPWA, isAuthenticated]);

  const { isLoading, startLoading, endLoading, loadingIcon } = useLoading({
    initialState: true,
    loadingIconColor: "primary",
    loadingIconSize: 40,
  });

  const {
    isLoading: isLoggingOut,
    startLoading: startLoggingOut,
    endLoading: endLoggingOut,
  } = useLoading();

  const {
    isLoading: isLoggingIn,
    startLoading: startLoggingIn,
    endLoading: endLoggingIn,
    loadingIcon: loadingIconLoggingIn,
  } = useLoading();

  const {
    isLoading: isRegistering,
    startLoading: startRegistering,
    endLoading: endRegistering,
    loadingIcon: loadingIconRegistering,
  } = useLoading();

  const {
    isLoading: isLoadingSendingEmail,
    startLoading: startSendingEmail,
    endLoading: endSendingEmail,
    loadingIcon: loadingIconSendingEmail,
  } = useLoading();

  const isLoginPage = pathname.includes(ROUTE_URLS.loginBase);
  const isRegisterPage = pathname.includes(ROUTE_URLS.registerBase);
  const isTermsPage = pathname.includes(ROUTE_URLS.termsBase);
  const isPrivacyPage = pathname.includes(ROUTE_URLS.privacyBase);
  const isEmailConfirmationPage = pathname.includes(ROUTE_URLS.emailConfirmationBase);
  const isEmailValidationPage = pathname.includes(ROUTE_URLS.emailValidationBase);

  const redirectUser = useCallback(
    (path: string) => {
      setRedirectUrlLogout(path);
      router(path, { replace: true });
    },
    [router]
  );

  const logout = useCallback(
    (redirect?: string) => {
      startLoggingOut();

      localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
      localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);

      setIsAuthenticated(false);

      const shouldRedirectToLogin =
        !isLoginPage && !isRegisterPage && !isTermsPage && !isPrivacyPage;
      const redirectPath = redirect || (shouldRedirectToLogin ? ROUTE_URLS.login(lang) : "");

      if (redirectPath) {
        redirectUser(redirectPath);
      }

      endLoggingOut();
    },
    [
      startLoggingOut,
      endLoggingOut,
      isLoginPage,
      isRegisterPage,
      isTermsPage,
      isPrivacyPage,
      lang,
      redirectUser,
    ]
  );

  // Escutar evento de token expirado disparado pelos interceptors do axios
  useEffect(() => {
    const handleTokenExpired = () => {
      logSessionEvent("evento tokenExpired recebido");
      logout();
    };

    window.addEventListener("tokenExpired", handleTokenExpired);

    return () => {
      window.removeEventListener("tokenExpired", handleTokenExpired);
    };
  }, [logout]);

  const handleIdleTimeout = useCallback(() => {
    logSessionEvent("idle timeout (inatividade)");
    logout();
  }, [logout]);

  useIdleTimeout(handleIdleTimeout, isAuthenticated);
  useProactiveTokenRefresh(isAuthenticated);

  const getUser = useCallback(
    async (userId: number) => {
      try {
        const fetched = await userService.getById(userId);

        if (!fetched) {
          throw new UserNotFound();
        }

        // Normalize shape used by permission checks (userProfile must be an array).
        const normalizedUser = {
          ...fetched,
          userProfile: fetched.userProfile ?? [],
          permissions: fetched.permissions ?? [],
        } as unknown as IUser;

        setUser(normalizedUser);

        if (fetched.languageCode) {
          localStorage.setItem(LOCAL_STORAGE.lang, fetched.languageCode as string);
        }
      } catch (err) {
        buildErrorMessage(err);
      }
    },
    [setUser]
  );

  const register = async ({ formData, onError, onSuccess }: RegisterProps): Promise<void> => {
    startRegistering();

    try {
      await authService.register(formData);

      await login({
        formData: { login: formData.email, password: formData.password },
        onError: () => {
          throw new Error("Erro ao tentar fazer login após o registro.");
        },
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      const message = buildErrorMessage(err);

      showToast({ text: message, type: "error" });

      if (onError) onError();
    } finally {
      endRegistering();
    }
  };

  const login = async ({ formData, onError, onSuccess }: LoginProps): Promise<void> => {
    startLoggingIn();

    try {
      const { accessToken, refreshToken, expiredPassword } = await authService.login(formData);

      if (!accessToken) throw new InvalidTokenError();

      localStorage.setItem(LOCAL_STORAGE.accessTokenKey, accessToken);
      if (refreshToken) {
        localStorage.setItem(LOCAL_STORAGE.refreshTokenKey, refreshToken);
      }

      const decodedToken = decodeToken(accessToken, setIsEmailConfirmed);

      if (checkIfTokenIsExpired(decodedToken)) throw new ExpiredTokenError();

      const user = await fetchUser(Number(decodedToken.UserID));

      if (user.languageCode) localStorage.setItem(LOCAL_STORAGE.lang, user.languageCode);

      setUser(user);

      setIsAuthenticated(true);
      setIsPasswordExpired(expiredPassword);
      localStorage.setItem(LOCAL_STORAGE.overduePlanBannerClosed, "false");
      localStorage.setItem(LOCAL_STORAGE.pendingPaymentBannerClosed, "false");
      handleUserRedirects(router, lang, isEmailConfirmed, urlToRedirect, expiredPassword);

      // PWA: Offer biometric activation after successful login
      if (isPWA && !biometricAuthService.isEnabled()) {
        biometricAuthService.isAvailable().then((available) => {
          if (available) setShowBiometricPrompt(true);
        });
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      logout();

      const message = buildErrorMessage(err);

      showToast({ text: message, type: "error" });
      if (onError) onError();
    } finally {
      endLoggingIn();
    }
  };

  const confirmEmail = async (props: ConfirmEmailProps) => {
    const { token } = props;

    try {
      const decodedToken: IDecodedToken = jwt_decode(token);

      if (!decodedToken || !decodedToken.CustomerId || !decodedToken.UserID) {
        throw new InvalidTokenError();
      }

      const isTokenExpired = checkIfTokenIsExpired(decodedToken);
      if (isTokenExpired) {
        throw new ExpiredTokenError();
      }

      await authService.confirmEmail(token, decodedToken.CustomerId, decodedToken.UserID);
      logout();
    } catch (error) {
      const message = buildErrorMessage(error);
      showToast({ text: message, type: "error" });
    }
  };

  const sendConfirmationEmail = async (userId: string) => {
    const accessToken = getAccessToken();

    if (!accessToken) {
      throw new InvalidTokenError();
    }
    const decodedToken = generateDecodedToken(accessToken, setIsEmailConfirmed);

    if (!decodedToken) {
      throw new InvalidTokenError();
    }

    startSendingEmail();

    try {
      await authService.sendConfirmationEmail(accessToken, decodedToken?.CustomerId, userId);
      showToast({ text: "E-mail de confirmação reenviado com sucesso.", type: "success" });
    } catch (error) {
      const message = buildErrorMessage(error);
      showToast({ text: message, type: "error" });
    } finally {
      endSendingEmail();
    }
  };

  const checkPermissions = (requiredProfiles: UserProfile[]) => {
    const hasNoPermission = !requiredProfiles.some((profile) =>
      user?.userProfile?.some((userProfile) => userProfile.profileId === profile)
    );

    return hasNoPermission;
  };

  useEffect(() => {
    if (!localStorage) return;
    let cancelled = false;
    startLoading();

    const isAccessTokenValid = (token: string | null): boolean => {
      if (!token) return false;
      const decodedToken = generateDecodedToken(token, setIsEmailConfirmed);
      if (!decodedToken) return false;
      return !checkIfTokenIsExpired(decodedToken);
    };

    const resolveAuthState = async () => {
      let accessToken = getAccessToken();
      if (isAccessTokenValid(accessToken)) {
        if (!cancelled) {
          if (!isAuthenticated) setIsAuthenticated(true);
          endLoading();
          setIsAuthReady(true);
        }
        return;
      }

      const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);
      if (refreshToken) {
        try {
          const tokens = await authService.refreshToken(refreshToken);
          accessToken = tokens.accessToken;
          if (isAccessTokenValid(accessToken)) {
            if (!cancelled) {
              if (!isAuthenticated) setIsAuthenticated(true);
              endLoading();
              setIsAuthReady(true);
            }
            return;
          }
        } catch {
          localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
          localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
        }
      } else if (accessToken) {
        localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
      }

      if (!cancelled) {
        if (isAuthenticated) setIsAuthenticated(false);
        endLoading();
        setIsAuthReady(true);
      }
    };

    void resolveAuthState();
    return () => {
      cancelled = true;
    };
  }, [endLoading, startLoading, isAuthenticated, lang, isLoggingOut]);

  useEffect(() => {
    if (isAuthenticated && isLoginPage) {
      router(urlToRedirect, { replace: true });
    }

    if (
      !isAuthenticated &&
      !isEmailConfirmationPage &&
      !isEmailValidationPage &&
      !isLoginPage &&
      !isRegisterPage &&
      !isLoading &&
      !isTermsPage &&
      !isPrivacyPage
    ) {
      performUnauthenticatedRedirect(redirectUrlLogout, router, pathname, lang);
    }

    if (isAuthenticated && !isEmailConfirmed && !pathname.includes("emailConfirmation")) {
      router(ROUTE_URLS.emailValidation(lang), { replace: true });
    }
  }, [
    isAuthenticated,
    isLoginPage,
    isRegisterPage,
    isTermsPage,
    isPrivacyPage,
    lang,
    pathname,
    router,
    urlToRedirect,
    redirectUrlLogout,
    isLoading,
    isEmailConfirmed,
    isEmailConfirmationPage,
    isEmailValidationPage,
  ]);

  useEffect(() => {
    const isOnChangePasswordPage = pathname.includes(ROUTE_URLS.changePasswordExpiredBase);

    if (isPasswordExpired && isAuthenticated && !isOnChangePasswordPage) {
      router(ROUTE_URLS.changePasswordExpired(lang, true), { replace: true });
      showToast({
        text: "Senha expirada, altere a senha para ter acesso as outras páginas",
        type: "info",
      });
    }
  }, [isAuthenticated, isPasswordExpired, lang, router, pathname, showToast]);

  useEffect(() => {
    // This useEffect gets user and saves in the user state
    if (!isAuthenticated) return;
    if (!localStorage) return;
    const accessToken = getAccessToken();
    if (!accessToken) return;
    const decodedToken = generateDecodedToken(accessToken, setIsEmailConfirmed);
    if (!decodedToken) return;
    // Evitar chamada duplicada: se já temos user carregado com o mesmo ID, não buscar novamente
    if (user && Number(user.id) === Number(decodedToken.UserID)) {
      setIsAuthReady(true);
      return;
    }
    (async () => {
      try {
        await getUser(Number(decodedToken.UserID));
      } finally {
        setIsAuthReady(true);
      }
    })();
  }, [getUser, isAuthenticated, user]);

  // Debug para monitorar mudanças no user state
  useEffect(() => {}, [user]);

  const isLoadingPathProtected =
    (isLoading || !isAuthenticated) &&
    !isEmailConfirmationPage &&
    !isEmailValidationPage &&
    !isLoginPage &&
    !isRegisterPage &&
    !isTermsPage &&
    !isPrivacyPage;

  const isLoadingLoginPage = (isLoading || isAuthenticated) && isLoginPage;

  if (isLoadingLoginPage || isLoggingOut || isLoadingPathProtected) {
    return (
      <div
        style={{
          height: "var(--full-vh)",
          width: "var(--full-vw)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loadingIcon}
      </div>
    );
  }

  if (import.meta.env.DEV && user !== null) {
    // eslint-disable-next-line no-console
    // eslint-disable-next-line no-console
  }

  // Verificar se todas as funções necessárias estão definidas
  if (typeof login !== "function") {
    console.error("Login function is not properly defined in auth context");
  }
  if (typeof logout !== "function") {
    console.error("Logout function is not properly defined in auth context");
  }
  if (typeof register !== "function") {
    console.error("Register function is not properly defined in auth context");
  }

  const contextValue: AuthContextProps = {
    isLoading,
    isAuthenticated,
    isAuthReady,
    isLoggingOut,
    logout,
    loadingIcon,
    user,
    login,
    loadingIconLoggingIn,
    isLoggingIn,
    register,
    isRegistering,
    loadingIconRegistering,
    confirmEmail,
    isLoadingSendingEmail,
    loadingIconSendingEmail,
    sendConfirmationEmail,
    checkPermissions,
    isEmailConfirmed,
    isPasswordExpired,
    setUser,
  };

  // Verificar se o contextValue está sendo criado corretamente
  if (!contextValue || typeof contextValue.login !== "function") {
    console.error("AuthContext value is not properly created");
    throw new Error("Failed to create AuthContext value");
  }

  const handleBiometricActivate = async () => {
    if (!user) return;
    setIsBiometricAuthenticating(true);
    try {
      await biometricAuthService.register(String(user.id), user.name || "user");
      setShowBiometricPrompt(false);
    } finally {
      setIsBiometricAuthenticating(false);
    }
  };

  const handleBiometricUnlock = async () => {
    setIsBiometricAuthenticating(true);
    try {
      const success = await biometricAuthService.authenticate();
      if (success) {
        setIsBiometricTransitioning(true);
        // Wait for fade-out animation before removing lock screen
        setTimeout(() => {
          setIsBiometricLocked(false);
          setIsBiometricTransitioning(false);
        }, 500);
      }
    } finally {
      setIsBiometricAuthenticating(false);
    }
  };

  const handleBiometricFallback = () => {
    setIsBiometricLocked(false);
    biometricAuthService.disable();
    logout();
  };

  // Show biometric lock screen using the same Login layout
  if (isBiometricLocked) {
    return (
      <Grid
        container
        sx={{
          height: "var(--full-vh)",
          width: "100%",
          transition: "opacity 0.5s ease-out, transform 0.5s ease-out",
          opacity: isBiometricTransitioning ? 0 : 1,
          transform: isBiometricTransitioning ? "scale(1.03)" : "scale(1)",
        }}
      >
        <Grid display={{ xs: "none", md: "grid" }} size={{ lg: 7.5 }}>
          <LeftContent />
        </Grid>
        <Grid
          size={{ xs: 12, lg: 4.5 }}
          sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <MDBox
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            height={"100%"}
            width={"100%"}
            bgColor="primary"
            position={"relative"}
            zIndex={3}
          >
            <BiometricUnlockForm
              onUnlock={handleBiometricUnlock}
              onFallbackLogin={handleBiometricFallback}
              isAuthenticating={isBiometricAuthenticating}
            />
          </MDBox>
        </Grid>
      </Grid>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {isLoadingLoginPage || isLoggingOut || isLoadingPathProtected ? (
        <div
          style={{
            height: "var(--full-vh)",
            width: "var(--full-vw)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {loadingIcon}
        </div>
      ) : (
        <>
          {children}
          <BiometricPrompt
            open={showBiometricPrompt}
            onActivate={handleBiometricActivate}
            onDismiss={() => setShowBiometricPrompt(false)}
            isLoading={isBiometricAuthenticating}
          />
        </>
      )}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);

  // Verificar se o contexto está sendo fornecido corretamente
  if (!context || typeof context.login !== "function") {
    console.error("AuthContext is not properly provided or login function is missing");
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  return context;
};

export default useAuth;
